import { DataTable } from 'primereact/datatable';
import { Student, StudentsLists } from './AssignmentSidebarContainer';
import { FC, useState } from 'react';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { SelectedSkillForAssignment } from '../../models/Assignment';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { Class } from '../../models/Class';
import { sortClassesList } from '../../utils/sortClassesList';
import { AssignmentSidebarDatesForm } from './AssignmentSidebarDatesForm';

interface Props {
  studentsLists: StudentsLists;
  selectedSkillForAssignment: SelectedSkillForAssignment;
  fromDate: Date;
  toDate: Date | null;
  selectedClasses: Class[];
}

interface SkillColumn {
  key: string;
  name: string;
}

export const AssignmentSidebarPreview: FC<Props> = ({
  studentsLists,
  selectedSkillForAssignment,
  fromDate,
  toDate,
  selectedClasses,
}) => {
  const [classPage, setClassPage] = useState(0);
  const completeList = [
    ...studentsLists.alreadyMastered.list,
    ...studentsLists.buildingFoundations.list,
    ...studentsLists.readyToLearn.list,
  ].sort((a, b) => a.studentLastName.localeCompare(b.studentLastName, undefined, { sensitivity: 'base' }));
  const orderedClasses = sortClassesList(selectedClasses).filter(selectedClass =>
    completeList.some(student => student.classId === selectedClass.id),
  );
  const currentClass = orderedClasses[Math.min(classPage, Math.max(orderedClasses.length - 1, 0))];
  const currentClassStudents = completeList.filter(student => student.classId === currentClass?.id);
  const onlyTargetSkill = !!selectedSkillForAssignment.onlyTarget;

  const extractSkillsColumns = () => {
    if (selectedSkillForAssignment.onlyTarget) {
      return [
        {
          key: selectedSkillForAssignment.id,
          name: `${selectedSkillForAssignment.name} (${selectedSkillForAssignment.code})`,
        } as SkillColumn,
      ];
    }
    const skillColumns: SkillColumn[] = [];
    completeList.forEach(student => {
      student.skills.forEach(skill => {
        if (skillColumns.every(column => column.key !== skill.id)) {
          skillColumns.push({ key: skill.id, name: skill.name });
        }
      });
    });
    return skillColumns;
  };

  return (
    <div className="px-4">
      <div className="nc_assignment-class-card">
        <div className="flex align-items-center justify-content-between nc_assignment-class-card__header">
          <span className="font-semibold">{currentClass?.sectionName}</span>
          <div className="flex align-items-center">
            <span className="mr-2 text-color-secondary">
              Class {orderedClasses.length ? classPage + 1 : 0} of {orderedClasses.length}
            </span>
            <Button
              icon="nc_icon-nc_back icon-sm"
              rounded
              outlined
              className="nc_assignment-nav-button mr-1"
              aria-label="Previous class"
              disabled={classPage === 0}
              onClick={() => setClassPage(page => page - 1)}
            />
            <Button
              icon="nc_icon-nc_next icon-sm"
              rounded
              outlined
              className="nc_assignment-nav-button"
              aria-label="Next class"
              disabled={classPage >= orderedClasses.length - 1}
              onClick={() => setClassPage(page => page + 1)}
            />
          </div>
        </div>
        <DataTable
          value={currentClassStudents}
          dataKey="studentId"
          stripedRows
          scrollable={!onlyTargetSkill}
          scrollHeight={onlyTargetSkill ? undefined : '32vh'}
        >
          <Column
            frozen={!onlyTargetSkill}
            field="studentName"
            style={onlyTargetSkill ? { width: '50%' } : { width: '2rem', minWidth: '2rem' }}
            header={
              <span className="text-color-secondary font-normal white-space-nowrap">
                {currentClassStudents.length} {currentClassStudents.length === 1 ? 'student' : 'students'}
              </span>
            }
            body={(st: Student) => (
              <div className="flex align-items-center white-space-nowrap">
                <MaskedStudentData type={PiiTypes.FullName} student={st}>
                  {`${st.studentFirstName} ${st.studentLastName}`}
                </MaskedStudentData>{' '}
                {st.noPredPassed && <i className="nc_icon-nc_warning ml-1" style={{ color: '#FDB814' }} />}
              </div>
            )}
          />
          {extractSkillsColumns().map(skColumn => {
            const isTarget = skColumn.key === selectedSkillForAssignment.id;
            return (
              <Column
                className="border-left-1"
                style={onlyTargetSkill ? { width: '50%' } : undefined}
                header={
                  <div className={`flex flex-column gap-2 ${onlyTargetSkill ? '' : 'nc_assignment-skill-header'}`}>
                    <span className={`nc_assignment-skill-tag ${isTarget ? '' : 'nc_assignment-skill-tag--support'}`}>
                      {isTarget ? 'Target' : 'Support'}
                    </span>
                    <span
                      className={`${onlyTargetSkill ? '' : 'nc_assignment-skill-name'} ${isTarget ? 'font-bold' : ''}`}
                      title={skColumn.name}
                    >
                      {skColumn.name}
                    </span>
                  </div>
                }
                key={skColumn.key}
                body={(student: Student) => {
                  const studentSkill = student.skills.find(sk => sk.id === skColumn.key);
                  return student.selected &&
                    (!studentSkill?.passed || studentSkill.id === selectedSkillForAssignment.id)
                    ? 'Assigned'
                    : 'Not Assigned';
                }}
              />
            );
          })}
        </DataTable>
      </div>

      <AssignmentSidebarDatesForm
        toDate={toDate ?? fromDate}
        setToDate={() => {}}
        fromDate={fromDate}
        setFromDate={() => {}}
        disabled
      />
    </div>
  );
};
